import { PDFDict, PDFDocument, PDFFont, PDFName, PDFNumber, PDFPage, rgb } from 'pdf-lib';
import {
  ACCESSORY_ROWS,
  INITIALS_PLACEMENT,
  MARGIN_X,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  QUANTITY_FIRST_LINE_Y,
  QUANTITY_LINE_GAP,
  QUANTITY_ROWS,
  QUANTITY_VALUE_X,
  SIGNATURE_DIVIDER_X,
  SIGNATURE_PAGE_INDEX,
  SIGNATURE_ROW_NORMAL_HEIGHT,
  SIGNATURE_ROW_TALL_HEIGHT,
  SIGNATURE_TABLE_TOP_Y,
  VALUE_FONT_SIZE,
  catalogRowSuffix,
  type ServiceAgreementPlacement,
} from './serviceAgreementLayout';
import {
  readQty,
  unitPriceFor,
  type ContractQuantities,
  type ContractUnitPrices,
} from './contractPricing';

/**
 * Everything on the agreement that depends on the individual deal rather than
 * the static template: the catalog lines' prices, the itemised accessories,
 * products added beyond the catalog, addendum pages, and a second Client
 * signature box.
 *
 * The template PDF (`public/agreementLocation.pdf` /
 * `public/trialAgreementLocation.pdf`) is generated with fixed coordinates by
 * `scripts/generateServiceAgreement.mjs`. On page 1 it prints only the heading
 * and the eleven "Qty ____" stems of the monthly list: the prices are left off
 * because a contract may negotiate its own (see `drawCatalogLines`), and the
 * paragraphs under the list are left off because products added to the
 * contract continue the list and push them down (see `drawPage1Extras`).
 * Everything here is drawn at fill time by both `OrderDetail.tsx` (team
 * preview) and `Contract.tsx` (client signing), which is why it lives in a
 * shared module.
 */

/** A product added to a contract on top of the pre-printed catalog lines. */
export interface ContractCustomProduct {
  id: string;
  name: string;
  /** Price per unit, in dollars. */
  unitPrice: number;
  /** Kept as a string to match the pre-printed `qty*` form fields. */
  qty: string;
  /** `monthly` rolls into Monthly Robotic Service Cost, `accessory` into Additional Accessories Cost. */
  section: 'monthly' | 'accessory';
  /** Set when the name was picked from the inventory catalog. */
  inventoryProductId?: string;
  /** Write-ins the user asked to file in the inventory catalog when the contract is generated. */
  addToInventory?: boolean;
  inventoryType?: 'Robot' | 'Accessory' | 'Stock Item';
}

/**
 * Page 1 below the pre-printed list, top to bottom: monthly products added to
 * the contract, continuing the list under Kebb-i; the intro paragraphs, one
 * list line lower for each of those; then the extras blocks (itemised
 * accessories, then one-time products added to the contract). Lines copy the
 * pre-printed geometry exactly, so an added line reads identically to the
 * Kebb-i line. Anything past the bottom spills onto a revision page.
 */
/** Baseline of the template's last monthly line (Kebb-i). */
const LIST_LAST_LINE_Y = QUANTITY_FIRST_LINE_Y - (QUANTITY_ROWS.length - 1) * QUANTITY_LINE_GAP;
/** First baseline of the intro paragraphs while nothing extends the list. */
const INTRO_TOP_Y = LIST_LAST_LINE_Y - QUANTITY_LINE_GAP - 18;
const INTRO_SIZE = 10;
const INTRO_LINE_HEIGHT = 12;
const INTRO_PARAGRAPH_GAP = 3;
const INTRO_MAX_WIDTH = PAGE_WIDTH - MARGIN_X * 2;
/** The paragraph after the initials blank starts this far below it. */
const INTRO_AFTER_INITIALS_GAP = 16;

/**
 * The template's wording, split around the "Check initial to acknowledge"
 * line. That line stays on INITIALS_PLACEMENT (moved with the list), because
 * the signing page writes the client's initials onto its blank.
 */
const INTRO_BEFORE_INITIALS = [
  "Meeting to walk and map designated pick-up and drop-off areas agreed to for the robot(s)'s functionality. " +
    'This is to facilitate the agreed drop-off and pickup points.',
  'The cost of the Contracted Elevator Technician for all necessary upgrades to the elevator system(s) is based on each location and its existing equipment. ' +
    'All third-party costs to be paid by Client to their vendor. *All 3rd party elevator expense is paid directly from Client to elevator company, ' +
    'budget $4,000-$15,000. This expense can be added into your monthly service payment. ' +
    'TechForce to manage and coordinate with third-party vendor at no cost to Client. ' +
    'Annual increases shall be 2% - 5% per year on anniversary date.',
  'Third-party electrician will alter all necessary access doors and any power requirements to power the robot(s). ' +
    "This will include a minimum automatic door opener for the robot(s)'s operation. " +
    'All third-party costs to be paid by Client to their vendor.',
];
const INTRO_AFTER_INITIALS = [
  'TechForce to manage and coordinate with third-party vendor at no cost to Client.',
  "During the implementation period, the Location will provide TechForce and its vendors with reasonable access to the areas required for the robot(s)' implementation. " +
    'The robot(s) will also need permanent space for the proper function, use, travel and storage.',
];

/** Heading for added monthly lines that no longer fit on page 1. */
const LIST_CONTINUED_HEADING = 'Robot Quantity, Model and Monthly Cost per Item (continued)';

/** The first extras heading sits this far below the intro's last line. */
const EXTRA_BLOCK_TOP_GAP = 29;
const EXTRA_BLOCK_MIN_Y = 72;
/** Distance from a block heading down to its first line. */
const EXTRA_HEADING_ADVANCE = 22;
const EXTRA_HEADING_SIZE = 12;
/** Breathing room between two blocks. */
const EXTRA_BLOCK_GAP = 10;

/** Geometry of a "Qty ___ : Label (suffix)" line, matching the template. */
const CATALOG_LINE_LABEL_X = 90;
const CATALOG_LINE_UNDERLINE_FROM_X = 60;
const CATALOG_LINE_UNDERLINE_TO_X = 86;
const CATALOG_LINE_SIZE = 10;
const CATALOG_PAGE_INDEX = 1;

/** Revision pages (addendums / overflowing extras) are inserted just before the signature page. */
export const REVISION_PAGE_INSERT_INDEX = SIGNATURE_PAGE_INDEX;

const REVISION_PAGE_TOP_Y = PAGE_HEIGHT - 60;
const REVISION_PAGE_BOTTOM_Y = 60;
const REVISION_BODY_SIZE = 10;
const REVISION_BODY_LINE_HEIGHT = 13;
const REVISION_MAX_WIDTH = PAGE_WIDTH - MARGIN_X * 2;

/**
 * Second Client signature box, drawn under the pre-printed Authorization table
 * (which ends at y=480) in the Client column so it reads as an additional
 * signatory for the same party.
 */
/** Bottom border of the pre-printed Authorization table (rows: 20, 20, 60, 20, 20). */
const SIGNATURE_TABLE_BOTTOM_Y =
  SIGNATURE_TABLE_TOP_Y - (4 * SIGNATURE_ROW_NORMAL_HEIGHT + SIGNATURE_ROW_TALL_HEIGHT);
const SECOND_SIG_TABLE_TOP_Y = SIGNATURE_TABLE_BOTTOM_Y - 24;
const SECOND_SIG_ROW_HEIGHTS = [
  SIGNATURE_ROW_NORMAL_HEIGHT, // 0: header
  SIGNATURE_ROW_TALL_HEIGHT, //   1: signature
  SIGNATURE_ROW_NORMAL_HEIGHT, // 2: name
  SIGNATURE_ROW_NORMAL_HEIGHT, // 3: position
];

function secondSigRowTop(rowIdx: number): number {
  let top = SECOND_SIG_TABLE_TOP_Y;
  for (let i = 0; i < rowIdx; i++) top -= SECOND_SIG_ROW_HEIGHTS[i];
  return top;
}

const SECOND_SIG_SIGNATURE_ROW_BOTTOM =
  secondSigRowTop(1) - SIGNATURE_ROW_TALL_HEIGHT;

/**
 * Placements for the second Client signature. No `pageIndex` — the signature
 * page moves when addendum pages are inserted, so callers resolve the page with
 * `shiftPageIndex`.
 */
export const SECOND_CLIENT_SIGNATURE_PLACEMENTS = {
  signature: {
    x: SIGNATURE_DIVIDER_X + 74,
    y: SECOND_SIG_SIGNATURE_ROW_BOTTOM + 4,
    width: 180,
    height: 44,
  },
  name: {
    x: SIGNATURE_DIVIDER_X + 44,
    y: secondSigRowTop(2) - 14,
    fontSize: VALUE_FONT_SIZE,
  },
  position: {
    x: SIGNATURE_DIVIDER_X + 54,
    y: secondSigRowTop(3) - 14,
    fontSize: VALUE_FONT_SIZE,
  },
};

/** Replace smart punctuation with ASCII so Helvetica can render it. */
function clean(text: string): string {
  return String(text ?? '')
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/\u2122/g, '(TM)')
    .replace(/\u00A0/g, ' ');
}

/** Word-wrap text to a target width (pt), honoring explicit line breaks. */
function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const rawLine of clean(text).split(/\r?\n/)) {
    const words = rawLine.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      continue;
    }
    let current = '';
    for (const word of words) {
      const candidate = current ? current + ' ' + word : word;
      if (font.widthOfTextAtSize(candidate, size) > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

/** Shorten `text` with a trailing ellipsis until it fits `maxWidth`. */
function truncateToWidth(text: string, font: PDFFont, size: number, maxWidth: number): string {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && font.widthOfTextAtSize(`${out}...`, size) > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out.trimEnd()}...`;
}

/** "3000" -> "3,000", "47.5" -> "47.50". Matches the pre-printed price suffixes. */
function formatUnitPrice(price: number): string {
  if (!Number.isFinite(price)) return '0';
  return Number.isInteger(price)
    ? price.toLocaleString('en-US')
    : price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** The pre-printed suffix style: "($300 each)", with one-time items called out. */
export function customProductSuffix(item: ContractCustomProduct): string {
  const price = `$${formatUnitPrice(item.unitPrice)}`;
  return item.section === 'accessory' ? `(${price} each, one-time)` : `(${price} each)`;
}

/** Read `customProducts` back off a contract's stored form data. */
export function parseCustomProducts(value: unknown): ContractCustomProduct[] {
  const raw = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!Array.isArray(raw)) return [];
  const items: ContractCustomProduct[] = [];
  raw.forEach((entry, idx) => {
    if (!entry || typeof entry !== 'object') return;
    const row = entry as Record<string, unknown>;
    const name = String(row.name ?? '').trim();
    if (!name) return;
    const unitPrice = Number(String(row.unitPrice ?? '').toString().replace(/[$,]/g, ''));
    items.push({
      id: String(row.id ?? `custom-${idx}`),
      name,
      unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
      qty: String(row.qty ?? '').trim(),
      section: row.section === 'accessory' ? 'accessory' : 'monthly',
      inventoryProductId: row.inventoryProductId ? String(row.inventoryProductId) : undefined,
      addToInventory: row.addToInventory === true,
      inventoryType:
        row.inventoryType === 'Robot' || row.inventoryType === 'Accessory' || row.inventoryType === 'Stock Item'
          ? row.inventoryType
          : undefined,
    });
  });
  return items;
}

/** Read `addendums` back off a contract's stored form data. */
export function parseAddendums(value: unknown): string[] {
  const raw = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => String(entry ?? '').trim()).filter(Boolean);
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** Sum of qty × unit price for one section, used for the contract cost totals. */
export function customProductsTotal(
  items: ContractCustomProduct[],
  section: 'monthly' | 'accessory'
): number {
  return items.reduce((total, item) => {
    if (item.section !== section) return total;
    const qty = parseInt(item.qty, 10) || 0;
    const price = Number.isFinite(item.unitPrice) ? item.unitPrice : 0;
    return total + qty * price;
  }, 0);
}

/**
 * Draw the label and price onto each pre-printed "Qty ____" stem of the monthly
 * list. The template deliberately leaves these blank so the price can follow
 * whatever this contract negotiated; with no overrides the output is identical
 * to the prices that used to be baked into the template.
 *
 * The quantity itself is still drawn from TEXT_PLACEMENTS by the caller.
 */
export function drawCatalogLines(
  page: PDFPage,
  unitPrices: ContractUnitPrices | null | undefined,
  font: PDFFont
): void {
  QUANTITY_ROWS.forEach((row, idx) => {
    const suffix = catalogRowSuffix(row, unitPriceFor(row.key, unitPrices));
    page.drawText(`: ${clean(row.label)} ${suffix}`, {
      x: CATALOG_LINE_LABEL_X,
      y: QUANTITY_FIRST_LINE_Y - idx * QUANTITY_LINE_GAP,
      size: CATALOG_LINE_SIZE,
      font,
      color: rgb(0, 0, 0),
    });
  });
}

/** Page index of the monthly quantity list, for callers resolving pages. */
export const CATALOG_LINES_PAGE_INDEX = CATALOG_PAGE_INDEX;

/** One "Qty ___ : Label (suffix)" line drawn outside the pre-printed list. */
interface ExtraLine {
  label: string;
  suffix: string;
  /** 0 leaves the blank empty, like a pre-printed line nobody filled in. */
  qty: number;
}

/** A headed group of extra lines, e.g. the itemised accessories. */
interface ExtraBlock {
  heading: string;
  /** Heading used when the block continues onto an inserted page. */
  continuedHeading: string;
  lines: ExtraLine[];
}

/** Draw one extra line, copying the pre-printed stem so it matches the list above. */
function drawExtraLine(page: PDFPage, line: ExtraLine, y: number, font: PDFFont): void {
  page.drawText('Qty', { x: MARGIN_X, y, size: CATALOG_LINE_SIZE, font, color: rgb(0, 0, 0) });
  page.drawLine({
    start: { x: CATALOG_LINE_UNDERLINE_FROM_X, y: y - 1 },
    end: { x: CATALOG_LINE_UNDERLINE_TO_X, y: y - 1 },
    thickness: 0.5,
    color: rgb(0, 0, 0),
  });
  if (line.qty > 0) {
    page.drawText(String(line.qty), {
      x: QUANTITY_VALUE_X,
      y,
      size: CATALOG_LINE_SIZE,
      font,
      color: rgb(0, 0, 0),
    });
  }
  const text = `: ${clean(line.label)} ${line.suffix}`;
  const maxWidth = PAGE_WIDTH - MARGIN_X - CATALOG_LINE_LABEL_X;
  page.drawText(truncateToWidth(text, font, CATALOG_LINE_SIZE, maxWidth), {
    x: CATALOG_LINE_LABEL_X,
    y,
    size: CATALOG_LINE_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
}

/** The deal-specific parts of a contract that this module has to lay out. */
export interface ContractExtrasInput {
  /** The `qty*` values as typed on the form. */
  quantities: ContractQuantities;
  unitPrices?: ContractUnitPrices | null;
  customProducts: ContractCustomProduct[];
  addendums: string[];
}

/** How the extras divide between page 1 and the inserted revision pages. */
export interface ContractExtrasPlan {
  /** Monthly products added to the contract, printed as more lines of the list under Kebb-i. */
  listLines: ExtraLine[];
  page1: ExtraBlock[];
  overflow: ExtraBlock[];
  addendums: string[];
}

/**
 * Every product added to one section, as list lines. One still waiting for its
 * quantity prints with an empty blank, the same as the pre-printed lines.
 */
function customProductLines(
  items: ContractCustomProduct[],
  section: 'monthly' | 'accessory'
): ExtraLine[] {
  return items
    .filter((item) => item.section === section)
    .map((item) => ({
      label: item.name,
      suffix: customProductSuffix(item),
      qty: parseInt(item.qty, 10) || 0,
    }));
}

/** Accessories that were ordered, then one-time products added beyond the catalog. */
function buildExtraBlocks(input: ContractExtrasInput): ExtraBlock[] {
  const blocks: ExtraBlock[] = [];

  const accessories = ACCESSORY_ROWS.map((row) => ({
    label: row.label,
    suffix: catalogRowSuffix(row, unitPriceFor(row.key, input.unitPrices)),
    qty: readQty(input.quantities, row.key),
  })).filter((line) => line.qty > 0);
  if (accessories.length > 0) {
    blocks.push({
      heading: 'Additional Accessories',
      continuedHeading: 'Additional Accessories (continued)',
      lines: accessories,
    });
  }

  const custom = customProductLines(input.customProducts, 'accessory');
  if (custom.length > 0) {
    blocks.push({
      heading: 'Additional Products',
      continuedHeading: 'Additional Products (continued)',
      lines: custom,
    });
  }

  return blocks;
}

interface IntroLine {
  text: string;
  y: number;
}

/** Where each line of the intro paragraphs lands once the list grew by `shift` points. */
interface IntroLayout {
  before: IntroLine[];
  initialsY: number;
  after: IntroLine[];
  lastLineY: number;
}

function flowIntroParagraphs(paragraphs: string[], startY: number, font: PDFFont): IntroLine[] {
  const lines: IntroLine[] = [];
  let y = startY;
  for (const paragraph of paragraphs) {
    for (const text of wrapText(paragraph, font, INTRO_SIZE, INTRO_MAX_WIDTH)) {
      lines.push({ text, y });
      y -= INTRO_LINE_HEIGHT;
    }
    y -= INTRO_PARAGRAPH_GAP;
  }
  return lines;
}

function layoutIntro(font: PDFFont, shift: number): IntroLayout {
  const initialsY = INITIALS_PLACEMENT.y - shift;
  const before = flowIntroParagraphs(INTRO_BEFORE_INITIALS, INTRO_TOP_Y - shift, font);
  const after = flowIntroParagraphs(INTRO_AFTER_INITIALS, initialsY - INTRO_AFTER_INITIALS_GAP, font);
  return { before, initialsY, after, lastLineY: after[after.length - 1].y };
}

/**
 * Decide what fits in page 1's open space and what has to spill onto an
 * inserted page. Kept apart from drawing because pages must be inserted before
 * `getPages()` is called and therefore before anything can be drawn — so both
 * callers plan first, insert, then draw. `font` measures the intro paragraphs.
 */
export function planContractExtras(input: ContractExtrasInput, font: PDFFont): ContractExtrasPlan {
  const page1: ExtraBlock[] = [];
  const overflow: ExtraBlock[] = [];

  // Each added line pushes the intro down by one list line, so the list grows
  // only while the intro's last line still clears the bottom margin.
  const monthly = customProductLines(input.customProducts, 'monthly');
  const room = Math.floor((layoutIntro(font, 0).lastLineY - EXTRA_BLOCK_MIN_Y) / QUANTITY_LINE_GAP);
  const listLines = monthly.slice(0, Math.max(0, room));
  if (listLines.length < monthly.length) {
    overflow.push({
      heading: LIST_CONTINUED_HEADING,
      continuedHeading: LIST_CONTINUED_HEADING,
      lines: monthly.slice(listLines.length),
    });
  }

  let y = layoutIntro(font, listLines.length * QUANTITY_LINE_GAP).lastLineY - EXTRA_BLOCK_TOP_GAP;
  let spilled = overflow.length > 0;

  for (const block of buildExtraBlocks(input)) {
    if (spilled) {
      overflow.push(block);
      continue;
    }
    // A block only starts on page 1 if its heading and a line both fit.
    y -= EXTRA_HEADING_ADVANCE;
    const fits: ExtraLine[] = [];
    const rest: ExtraLine[] = [];
    for (const line of block.lines) {
      if (!spilled && y >= EXTRA_BLOCK_MIN_Y) {
        fits.push(line);
        y -= QUANTITY_LINE_GAP;
      } else {
        spilled = true;
        rest.push(line);
      }
    }
    if (fits.length === 0) {
      spilled = true;
      overflow.push(block);
      continue;
    }
    page1.push({ ...block, lines: fits });
    if (rest.length > 0) overflow.push({ ...block, heading: block.continuedHeading, lines: rest });
    y -= EXTRA_BLOCK_GAP;
  }

  return { listLines, page1, overflow, addendums: input.addendums.filter((text) => text.trim()) };
}

/** True when `insertContractRevisionPages` will add pages for this plan. */
export function planNeedsRevisionPages(plan: ContractExtrasPlan): boolean {
  return plan.overflow.length > 0 || plan.addendums.length > 0;
}

/**
 * Draw everything under the pre-printed list, in the order the plan measured
 * it: the added list lines, the intro paragraphs, then the extras blocks. The
 * added line count is also recorded on the document for
 * `clientInitialsPlacement`.
 */
export function drawPage1Extras(
  page: PDFPage,
  plan: ContractExtrasPlan,
  font: PDFFont,
  boldFont: PDFFont
): void {
  plan.listLines.forEach((line, idx) => {
    drawExtraLine(page, line, LIST_LAST_LINE_Y - (idx + 1) * QUANTITY_LINE_GAP, font);
  });

  const intro = layoutIntro(font, plan.listLines.length * QUANTITY_LINE_GAP);
  const drawIntroLine = (line: IntroLine) => {
    page.drawText(line.text, { x: MARGIN_X, y: line.y, size: INTRO_SIZE, font, color: rgb(0, 0, 0) });
  };
  intro.before.forEach(drawIntroLine);
  page.drawText('Check initial to acknowledge', {
    x: MARGIN_X,
    y: intro.initialsY,
    size: INTRO_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  page.drawLine({
    start: { x: INITIALS_PLACEMENT.x - 4, y: intro.initialsY - 2 },
    end: { x: INITIALS_PLACEMENT.x + 50, y: intro.initialsY - 2 },
    thickness: 0.5,
    color: rgb(0, 0, 0),
  });
  page.drawText('. Cost may vary based upon needs.', {
    x: INITIALS_PLACEMENT.x + 54,
    y: intro.initialsY,
    size: INTRO_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  intro.after.forEach(drawIntroLine);

  recordListLineCount(page.doc, plan.listLines.length);

  let y = intro.lastLineY - EXTRA_BLOCK_TOP_GAP;
  for (const block of plan.page1) {
    page.drawText(block.heading, {
      x: MARGIN_X,
      y,
      size: EXTRA_HEADING_SIZE,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    y -= EXTRA_HEADING_ADVANCE;
    for (const line of block.lines) {
      drawExtraLine(page, line, y, font);
      y -= QUANTITY_LINE_GAP;
    }
    y -= EXTRA_BLOCK_GAP;
  }
}

/** Custom document-info entry; viewers don't list it, and it survives the PDF editor's re-save. */
const LIST_LINE_COUNT_KEY = PDFName.of('TechForceAddedListLines');

function recordListLineCount(pdfDoc: PDFDocument, count: number): void {
  const { context } = pdfDoc;
  const existing = context.lookup(context.trailerInfo.Info);
  const info = existing instanceof PDFDict ? existing : context.obj({});
  if (info !== existing) context.trailerInfo.Info = context.register(info);
  info.set(LIST_LINE_COUNT_KEY, PDFNumber.of(count));
}

function recordedListLineCount(pdfDoc: PDFDocument): number {
  const { context } = pdfDoc;
  const info = context.lookup(context.trailerInfo.Info);
  const value = info instanceof PDFDict ? info.get(LIST_LINE_COUNT_KEY) : undefined;
  return value instanceof PDFNumber ? Math.max(0, Math.round(value.asNumber())) : 0;
}

/**
 * Where the client's initials go on a filled contract. Lines added to the
 * monthly list move the blank down, and a stored contract may have been edited
 * after it was filled, so the count is read from the PDF itself rather than
 * the contract's form data. A PDF without the count has the template layout.
 */
export function clientInitialsPlacement(pdfDoc: PDFDocument): ServiceAgreementPlacement {
  return {
    ...INITIALS_PLACEMENT,
    y: INITIALS_PLACEMENT.y - recordedListLineCount(pdfDoc) * QUANTITY_LINE_GAP,
  };
}

/**
 * Insert the revision page(s) immediately before the signature page and draw
 * the addendums, plus whatever extras overflowed page 1, onto them.
 *
 * Returns how many pages were added, which every placement on the signature
 * page must be shifted by. Pass the result through `shiftPageIndex`.
 */
export function insertContractRevisionPages(
  pdfDoc: PDFDocument,
  plan: ContractExtrasPlan,
  font: PDFFont,
  boldFont: PDFFont
): number {
  const { addendums, overflow } = plan;
  if (addendums.length === 0 && overflow.length === 0) return 0;

  let inserted = 0;
  let page = pdfDoc.insertPage(REVISION_PAGE_INSERT_INDEX + inserted, [PAGE_WIDTH, PAGE_HEIGHT]);
  inserted += 1;
  let y = REVISION_PAGE_TOP_Y;

  const nextPage = () => {
    page = pdfDoc.insertPage(REVISION_PAGE_INSERT_INDEX + inserted, [PAGE_WIDTH, PAGE_HEIGHT]);
    inserted += 1;
    y = REVISION_PAGE_TOP_Y;
  };

  const ensureRoom = (needed: number) => {
    if (y - needed < REVISION_PAGE_BOTTOM_Y) nextPage();
  };

  const drawBlockHeading = (text: string, size: number) => {
    ensureRoom(size + 8);
    page.drawText(clean(text), { x: MARGIN_X, y, size, font: boldFont, color: rgb(0, 0, 0) });
    y -= size + 8;
  };

  const drawBody = (text: string) => {
    for (const line of wrapText(text, font, REVISION_BODY_SIZE, REVISION_MAX_WIDTH)) {
      ensureRoom(REVISION_BODY_LINE_HEIGHT);
      if (line) {
        page.drawText(line, {
          x: MARGIN_X,
          y,
          size: REVISION_BODY_SIZE,
          font,
          color: rgb(0, 0, 0),
        });
      }
      y -= REVISION_BODY_LINE_HEIGHT;
    }
  };

  for (const block of overflow) {
    drawBlockHeading(block.heading, 13);
    for (const line of block.lines) {
      ensureRoom(QUANTITY_LINE_GAP);
      drawExtraLine(page, line, y, font);
      y -= QUANTITY_LINE_GAP;
    }
    y -= 12;
  }

  if (addendums.length > 0) {
    drawBlockHeading(addendums.length > 1 ? 'Addendums' : 'Addendum', 13);
    addendums.forEach((text, idx) => {
      drawBlockHeading(`Addendum ${idx + 1}`, 10);
      drawBody(text);
      y -= 8;
    });
  }

  return inserted;
}

/**
 * Adjust a template page index for pages inserted before the signature page.
 * Placements above the insertion point are unaffected.
 */
export function shiftPageIndex(pageIndex: number, insertedPages: number): number {
  return pageIndex >= REVISION_PAGE_INSERT_INDEX ? pageIndex + insertedPages : pageIndex;
}

const CLIENT_HEADING_MAX_WIDTH = PAGE_WIDTH - MARGIN_X - (SIGNATURE_DIVIDER_X + 8) - 6;

function fitClientHeading(companyName: string, font: PDFFont): { text: string; size: number } {
  const name = clean(companyName).replace(/\s+/g, ' ').trim();
  for (const size of [11, 10, 9, 8]) {
    try {
      if (font.widthOfTextAtSize(name, size) <= CLIENT_HEADING_MAX_WIDTH) return { text: name, size };
    } catch {
      break;
    }
  }
  let clipped = name;
  while (clipped.length > 1) {
    const candidate = `${clipped}...`;
    try {
      if (font.widthOfTextAtSize(candidate, 8) <= CLIENT_HEADING_MAX_WIDTH) return { text: candidate, size: 8 };
    } catch {
      break;
    }
    clipped = clipped.slice(0, -1);
  }
  return { text: clipped || name, size: 8 };
}

/**
 * Draw the company name in the Authorization heading cell.
 * Older templates still print `Business Name "Client"` there; the white
 * rectangle covers that label so only the company name remains.
 */
export function drawClientAuthorizationName(page: PDFPage, boldFont: PDFFont, companyName: string | undefined): void {
  const name = String(companyName ?? '').trim();
  const fitted = name
    ? fitClientHeading(name, boldFont)
    : { text: 'Business Name "Client"', size: 11 };
  page.drawRectangle({
    x: SIGNATURE_DIVIDER_X + 1.5,
    y: SIGNATURE_TABLE_TOP_Y - SIGNATURE_ROW_NORMAL_HEIGHT + 1.5,
    width: PAGE_WIDTH - MARGIN_X - SIGNATURE_DIVIDER_X - 3,
    height: SIGNATURE_ROW_NORMAL_HEIGHT - 3,
    color: rgb(1, 1, 1),
    borderWidth: 0,
  });
  page.drawText(fitted.text, {
    x: SIGNATURE_DIVIDER_X + 8,
    y: SIGNATURE_TABLE_TOP_Y - 14,
    size: fitted.size,
    font: boldFont,
    color: rgb(0, 0, 0),
  });
}

/** Empty second Client signature box under the Authorization table. */
export function drawSecondClientSignatureBox(
  page: PDFPage,
  font: PDFFont,
  boldFont: PDFFont,
  companyName?: string
): void {
  const left = SIGNATURE_DIVIDER_X;
  const right = PAGE_WIDTH - MARGIN_X;
  const totalHeight = SECOND_SIG_ROW_HEIGHTS.reduce((sum, h) => sum + h, 0);
  const bottom = SECOND_SIG_TABLE_TOP_Y - totalHeight;

  page.drawRectangle({
    x: left,
    y: bottom,
    width: right - left,
    height: totalHeight,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  let dividerY = SECOND_SIG_TABLE_TOP_Y;
  for (let i = 0; i < SECOND_SIG_ROW_HEIGHTS.length - 1; i++) {
    dividerY -= SECOND_SIG_ROW_HEIGHTS[i];
    page.drawLine({
      start: { x: left, y: dividerY },
      end: { x: right, y: dividerY },
      thickness: 0.5,
      color: rgb(0, 0, 0),
    });
  }

  const label = (rowIdx: number, text: string, bold = false) => {
    page.drawText(text, {
      x: left + 8,
      y: secondSigRowTop(rowIdx) - 14,
      size: bold ? 11 : 10,
      font: bold ? boldFont : font,
      color: rgb(0, 0, 0),
    });
  };
  const heading = String(companyName ?? '').trim();
  if (heading) {
    const fitted = fitClientHeading(heading, boldFont);
    page.drawText(fitted.text, {
      x: left + 8,
      y: secondSigRowTop(0) - 14,
      size: fitted.size,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
  } else {
    label(0, 'Business Name "Client"', true);
  }
  label(1, 'Signature:');
  label(2, 'Name:');
  label(3, 'Position:');
}
